// src/components/private/UserAccount.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, ListGroup, Row, Col, Container, Button } from 'react-bootstrap';
import EditUserModal from './EditUserModal';
import { getCountryLabel } from '../common_components/choices';

const ViewUserAccount = () => {
    const [userInfo, setUserInfo] = useState(null);
    const token = localStorage.getItem('token');
    const [showEditModal, setShowEditModal] = useState(false); // State to control modal visibility

    // Retrieve token and user info from localStorage
    const userInfoContent = JSON.parse(localStorage.getItem('userInfo'));

    const fetchUserInfo = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/users/users/`, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            });
            if (response.data.length > 0) {
                const userData = response.data[0];
                setUserInfo(userData);
                
                // Check if user has geo_location - if not, automatically show edit modal
                if (!userData.has_geo_location) {
                    setShowEditModal(true);
                }
            }
        } catch (error) {
            console.error('Error fetching user info:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchUserInfo();
        document.title = 'Saarthi - User Account';
    }, [fetchUserInfo]);

    if (!userInfo) {
        return <div>Loading user information...</div>;
    }

    // Prepare the address parts in an array, filtering out any undefined or empty strings.
    const addressParts = [
        userInfo.address_line_1,
        userInfo.address_line_2 ? userInfo.address_line_2 + ', ' : '',
        `${userInfo.postal_code ? userInfo.postal_code + ', ' : ''} ${userInfo.city ? userInfo.city + ', ' : ''}`,
        `${userInfo.state ? userInfo.state + ', ' : ''} ${getCountryLabel(userInfo.country)}`,
    ].filter(Boolean); // This removes any falsy values from the array, e.g., undefined, null, or empty strings.

    return (
        <Container>
            <Row>
                <Col xs={6} md={6}>
                    <Card>
                        <Card.Body>
                            <Row>
                                <Col xs={9}>
                                    <Card.Title>Personal Information</Card.Title>
                                </Col>
                                <Col xs={3} className="text-end">
                                    <Button variant="link" onClick={() => setShowEditModal(true)}>Edit</Button>
                                </Col>
                            </Row>
                            <ListGroup variant="flush">
                                <ListGroup.Item>Username: {userInfo.username}</ListGroup.Item>
                                <ListGroup.Item>Email: {userInfo.email}</ListGroup.Item>
                                <ListGroup.Item>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            Address:
                                            <p>
                                                {addressParts.map((part, index) => (
                                                    <React.Fragment key={index}>
                                                        {part}{index < addressParts.length - 1 && <br />}
                                                    </React.Fragment>
                                                ))}
                                            </p>
                                            {!userInfo.has_geo_location && (
                                                <small className="text-warning">⚠️ Location not validated</small>
                                            )}
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            </ListGroup>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={6}>
                    <Card
                        style={{
                            backgroundColor: userInfoContent.tier_slug === 'essential' ? '#9bb045' : '#fcdebe', // Red for Essential, Green for Complete
                            color: userInfoContent.tier_slug === 'essential' ? '#721c24' : '#333333', // Text colors matching the background
                        }}
                    >
                        <Card.Body>
                            <Card.Title>Plan</Card.Title>
                            <p>{userInfoContent.tier_slug === 'essential' ? 'Essential' : 'Complete'}</p>
                        </Card.Body>
                    </Card>
                    <Card className="mt-3">
                        <Card.Body>
                            <Row>
                                <Col xs={9}>
                                    <Card.Title>Preferences</Card.Title>
                                </Col>
                            </Row>
                            <ListGroup variant="flush">
                                <ListGroup.Item>Language: {userInfo.preferred_language}</ListGroup.Item>
                                <ListGroup.Item>Currency: {userInfo.preferred_currency}</ListGroup.Item>
                                <ListGroup.Item>Unit of Measure: {userInfo.preferred_unit}</ListGroup.Item>
                                {/* Additional User Information */}
                            </ListGroup>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <EditUserModal
                show={showEditModal}
                handleClose={() => setShowEditModal(false)}
                user={userInfo}
                fetchUserInfo={fetchUserInfo}
            />
        </Container>
    );
};

export default ViewUserAccount;